"use client"

import type { CSSProperties } from "react"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/lib/i18n"
import { UserText } from "@/components/ui/user-text"
import { intlLocaleFor } from "@/components/user-portal/learner-i18n"
import { ChartCard, type ChartCardLocale } from "@/components/admin/assistant/chat/cards/chart-card"
import { CardPanel } from "@/components/admin/assistant/chat/cards/row-list"
import { HeatmapView } from "@/components/vcro/riskometer/heatmap-card"
import { formatScore } from "@/lib/vcro/format"
import { VCRO_LEVEL_TOKENS } from "@/lib/vcro/levels"
import type { ChatChartCard } from "@/lib/ai-chat/types"

/**
 * A series a vCRO tool returned, drawn in the answer (vCRO wave 4, W4-ASK).
 *
 * The admin assistant's chart card is the precedent and the engine: the same
 * Chart / Table switch, chart tokens and flat bar list (components/admin/
 * assistant/chat/cards), here with the tenant chat's four languages and
 * formats passed in (ChartCard's `locale`). The heatmap is the Riskometer's
 * own (HeatmapView), so a chart in the chat reads like the screen.
 *
 * Every figure is the API's, as the server's projection sent it (api
 * tools/chart-card.ts): a score is drawn as returned, a withheld bucket is
 * not drawn (the card says some were withheld), and the movers show "then"
 * and "now" side by side rather than a change computed here. A bucket name is
 * user text; a calendar day is the API's yyyy-MM-dd, never shifted by the
 * browser's zone.
 */

/** The console's chat tokens, which the admin cards read, in the tenant app's terms. */
const CHAT_TOKENS = {
  "--chat-radius": "calc(var(--radius) - 2px)",
  "--chat-shadow-md": "0 4px 12px -2px rgb(0 0 0 / 0.12)",
} as CSSProperties

/** The built-in dimensions are named by the catalogue; a category or custom field by its own (user) name. */
const BUILT_IN_DIMENSION_KEY: Record<string, string> = {
  department: "reports.dimensions.department",
  location: "reports.dimensions.location",
  designation: "reports.dimensions.designation",
}

function useChartLocale(category: string): ChartCardLocale {
  const { t, locale } = useTranslation()
  const intl = intlLocaleFor(locale)
  // A calendar day (yyyy-MM-dd) is a day, not an instant: read and printed in UTC so no zone moves it.
  const day = (options: Intl.DateTimeFormatOptions) => (value: string) => {
    const d = new Date(`${value.slice(0, 10)}T00:00:00Z`)
    return Number.isNaN(d.getTime()) ? value : new Intl.DateTimeFormat(intl, { ...options, timeZone: "UTC" }).format(d)
  }
  return {
    chart: t("vcro.ask.chart.chart"),
    table: t("vcro.ask.chart.table"),
    showAs: t("vcro.ask.chart.showAs"),
    noData: t("vcro.ask.chart.noData"),
    category,
    axisDate: day({ month: "short", day: "numeric" }),
    longDate: day({ year: "numeric", month: "short", day: "numeric" }),
    value: (v: number) => formatScore(v, locale),
    renderCategory: (label: string) =>
      label ? <UserText>{label}</UserText> : <span className="italic text-muted-foreground">{t("vcro.ask.chart.notSet")}</span>,
  }
}

function Frame({ kind, children }: { kind: ChatChartCard["kind"]; children: React.ReactNode }) {
  return (
    <div
      data-testid="chat-chart-card"
      data-kind={kind}
      className={cn("my-2 w-full min-w-0", VCRO_LEVEL_TOKENS)}
      style={CHAT_TOKENS}
    >
      {children}
    </div>
  )
}

function PersonTrend({ chart }: { chart: Extract<ChatChartCard, { kind: "vcroPersonTrend" }> }) {
  const { t } = useTranslation()
  const locale = useChartLocale(t("vcro.ask.chart.date"))
  return (
    <Frame kind={chart.kind}>
      <ChartCard
        title={t("vcro.ask.chart.personTrendTitle")}
        type="line"
        x_key="date"
        data={chart.points.map((p) => ({ date: p.date, score: p.score }))}
        series={[{ key: "score", label: t("vcro.ask.chart.score") }]}
        locale={locale}
      />
    </Frame>
  )
}

function Breakdown({ chart }: { chart: Extract<ChatChartCard, { kind: "vcroBreakdown" }> }) {
  const { t } = useTranslation()
  const locale = useChartLocale(t("vcro.ask.chart.group"))
  const builtIn = BUILT_IN_DIMENSION_KEY[chart.dimension]
  const dimension = builtIn ? t(builtIn) : chart.label
  // A withheld bucket has no score to draw; one with no score yet is not drawn either.
  const shown = chart.rows.filter((r) => r.hiddenReason === null && r.score !== null)
  const withheld = chart.rows.some((r) => r.hiddenReason !== null)
  return (
    <Frame kind={chart.kind}>
      <ChartCard
        title={t("vcro.ask.chart.breakdownTitle", { dimension })}
        type="bar"
        x_key="label"
        data={shown.map((r) => ({ label: r.label, score: r.score }))}
        series={[{ key: "score", label: t("vcro.ask.chart.score") }]}
        locale={locale}
      />
      {withheld ? (
        <p className="mt-1.5 text-xs text-muted-foreground" data-testid="chat-chart-withheld">
          {t("vcro.ask.chart.withheld")}
        </p>
      ) : null}
    </Frame>
  )
}

function Movers({ chart }: { chart: Extract<ChatChartCard, { kind: "vcroMovers" }> }) {
  const { t } = useTranslation()
  const locale = useChartLocale(t("vcro.ask.chart.group"))
  return (
    <Frame kind={chart.kind}>
      <ChartCard
        title={t("vcro.ask.chart.moversTitle")}
        type="bar"
        x_key="label"
        data={chart.buckets.map((b) => ({ label: b.label, from: b.from, to: b.to }))}
        series={[
          { key: "from", label: t("vcro.ask.chart.then") },
          { key: "to", label: t("vcro.ask.chart.now") },
        ]}
        locale={locale}
      />
    </Frame>
  )
}

function Heatmap({ chart }: { chart: Extract<ChatChartCard, { kind: "vcroHeatmap" }> }) {
  const { t } = useTranslation()
  const { kind, ...heatmap } = chart
  return (
    <Frame kind={kind}>
      <CardPanel title={t("vcro.ask.chart.heatmapTitle")}>
        <div className="p-2">
          <HeatmapView heatmap={heatmap} />
        </div>
      </CardPanel>
    </Frame>
  )
}

export function ChartResultCard({ chart }: { chart: ChatChartCard }) {
  switch (chart.kind) {
    case "vcroPersonTrend":
      return <PersonTrend chart={chart} />
    case "vcroBreakdown":
      return <Breakdown chart={chart} />
    case "vcroMovers":
      return <Movers chart={chart} />
    case "vcroHeatmap":
      return <Heatmap chart={chart} />
    default:
      // A kind this build does not know (a newer server): draw nothing rather than guess.
      return null
  }
}
